import { axiosInstance } from './axios';

let csrfToken: string | null = null;
let csrfInitialized = false;
let csrfInitPromise: Promise<void> | null = null;

/**
 * Fetch CSRF token from the backend
 * This should be called once on app initialization
 */
export async function initCsrf(): Promise<void> {
  // If already initialized, skip
  if (csrfInitialized) return;

  // If initialization is in progress, wait for it
  if (csrfInitPromise) return csrfInitPromise;

  csrfInitPromise = (async () => {
    try {
      const { data } = await axiosInstance.get<{ csrfToken: string }>('/api/csrf-token');
      csrfToken = data.csrfToken;
      csrfInitialized = true;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      // Clear the promise so a later initCsrf() can actually retry. Without
      // this, a single failure (e.g. the API restarting during a deploy) leaves
      // an already-resolved promise here while csrfInitialized stays false, so
      // every subsequent call returns instantly and the token is null for the
      // rest of the session.
      csrfInitPromise = null;
      // Don't throw - CSRF might not be required for all endpoints
      // The app should still work, just without CSRF protection
    }
  })();

  return csrfInitPromise;
}

/**
 * Get the current CSRF token
 */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/**
 * Refresh the CSRF token
 * Call this after login/logout or if you get a CSRF validation error
 */
export async function refreshCsrfToken(): Promise<void> {
  csrfInitialized = false;
  csrfInitPromise = null;
  await initCsrf();
}

/**
 * Check if CSRF token is initialized
 */
export function isCsrfInitialized(): boolean {
  return csrfInitialized;
}

/**
 * Whether a 403 body says the CSRF check failed, so the request is worth
 * replaying with a fresh token. The API sends a code (FST_CSRF_*); only an
 * older API without one is judged by its message.
 */
export function isCsrfFailure(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const { code, message } = body as { code?: unknown; message?: unknown };
  if (typeof code === 'string') return code.startsWith('FST_CSRF');
  return typeof message === 'string' && message.toLowerCase().includes('csrf');
}
